import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {finalize} from 'rxjs/operators';

import {InvoicesService} from '../invoices.service';
import {CellType} from '@components/ui/table-template/enums/cell-type.enum';
import {Alignment} from '@components/ui/table-template/enums/alignment.enum';
import {TableTemplateColumnConfig} from '@components/ui/table-template/models/table-template-column-config';
import {SortedTable} from '@shared/enums/sorted-table.enum';
import {SessionStorageService} from '@shared/services/session-storage.service';
import {InvoiceSummaryConverterService} from './view-models/invoice-summary-converter.service';
import {ClientsideDataService} from '@shared/services/clientside-data.service';
import {InvoiceSummaryViewModel} from './view-models/invoice-summary-view.model';
import {PageViewModel} from '@components/ui/table-template/models/page-view-model';
import {DataViewModel} from '@components/ui/table-template/models/data-view-model';
import {TableTemplateComponentHost} from '@components/ui/table-template/table-template-component-host';
import {Keys} from '@shared/keys';
import {TableTemplateComponent} from '@components/ui/table-template/table-template.component';
import {LoadingAnimationComponent} from '@shared/components/loading-animation/loading-animation.component';
import {InvoiceCardContainerComponent} from './invoice-card-container/invoice-card-container.component';

@Component({
    selector: 'app-view-invoice-summaries',
    templateUrl: './view-invoice-summaries.component.html',
    styleUrls: ['./view-invoice-summaries.component.scss'],
    providers: [ClientsideDataService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        TableTemplateComponent,
        LoadingAnimationComponent,
        InvoiceCardContainerComponent
    ]
})
export class ViewInvoiceSummariesComponent implements OnInit, TableTemplateComponentHost {

  private readonly SEARCH_FIELDS: Keys<InvoiceSummaryViewModel> = ['customerName'];

  private readonly invoicesService = inject(InvoicesService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly converter = inject(InvoiceSummaryConverterService);
  private readonly clientsideDataService = inject(ClientsideDataService<InvoiceSummaryViewModel>);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  dataLoading = signal(true);
  dataAvailable = signal(false);
  dataEmpty = signal(false);
  columns = signal<TableTemplateColumnConfig[]>([]);
  sortField = signal('totalRemainingAmount');
  sortOrder = signal(-1);
  searchString = signal('');
  pageNumber = signal(1);
  pageSize = signal(20);
  currentPage = signal<PageViewModel<InvoiceSummaryViewModel>>({
    totalElements: 0,
    pageNumber: 1,
    pageSize: 20,
    data: []
  });

  invoices: InvoiceSummaryViewModel[] = [];

  ngOnInit(): void {
    this.dataLoading.set(true);
    this.dataEmpty.set(false);
    this.dataAvailable.set(false);

    this.populateColumns();
    const storedSort = this.sessionStorageService.getSort(SortedTable.Invoices);
    if (typeof storedSort[0] === 'string' && typeof storedSort[1] === 'number') {
      this.sortField.set(storedSort[0]);
      this.sortOrder.set(storedSort[1]);
    }

    this.invoicesService.getInvoices()
      .pipe(
        finalize(() => {
          this.dataLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: invoices => {
          this.invoices = this.converter.convert(invoices);
          this.clientsideDataService.initialize(this.invoices);
          this.updateData();
          this.dataEmpty.set(this.invoices.length === 0);
          this.dataAvailable.set(!this.dataEmpty());
        },
        error: error => {
          console.error('Error loading invoices', error);
          this.dataEmpty.set(true);
          this.dataAvailable.set(false);
        }
      });
  }

  private updateData(): void {
    this.currentPage.set(this.clientsideDataService.generatePage(
      this.sortField(),
      this.sortOrder() === 1,
      this.SEARCH_FIELDS,
      this.searchString(),
      this.pageNumber(),
      this.pageSize()));
  }

  sort(column: TableTemplateColumnConfig): void {
    this.updateSortColumn(column.dataProperty);
    this.pageNumber.set(1);
    this.updateData();
  }

  private updateSortColumn(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() * -1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }
    this.sessionStorageService.setSort(SortedTable.Invoices, this.sortField(), this.sortOrder());
  }

  search(searchTerm: string): void {
    this.searchString.set(searchTerm);
    this.pageNumber.set(1);
    this.updateData();
  }

  page(targetPage: number): void {
    this.pageNumber.set(targetPage);
    this.updateData();
  }

  rowClicked(dataViewModel: DataViewModel): void {
    // NOTE: The rowClicked method is only used when the table template is clicked.
    // When an invoice summary card is clicked, the click is handled within that component.
    // Any change to this method's behaviour must be mirrored in the invoice summary card component.
    const vm = dataViewModel as InvoiceSummaryViewModel;
    this.router.navigate(vm.detailsRouterLinkCommands);
  }

  populateColumns(): void {
    this.columns.set([
      {
        title: 'Entity Name',
        dataProperty: 'customerName',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Left,
        width: '30%',
        dataFocusable: true,
        headingFocusable: true
      },
      {
        title: 'Invoice Count',
        dataProperty: 'invoiceCount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '12%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Currency',
        dataProperty: 'currency',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '13%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount',
        dataProperty: 'totalNetBillAmount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '16%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Status',
        dataProperty: 'statusIcon',
        cellType: CellType.Icon,
        sortable: true,
        alignment: Alignment.Center,
        width: '13%',
        dataFocusable: false,
        headingFocusable: true
      },
      {
        title: 'Amount Remaining',
        dataProperty: 'totalRemainingAmount',
        cellType: CellType.Text,
        sortable: true,
        alignment: Alignment.Right,
        width: '16%',
        dataFocusable: false,
        headingFocusable: true
      }
    ]);
  }
}
